import {v4 as uuidv4} from "uuid"
import {Result} from "neverthrow"
import {createResource, getResource, OkDocument, ErrorDocument} from "./common"

export enum IntentStatus {
	Created = "Created",
	Approved = "Approved",
	Declined = "Declined",
	Processing = "Processing",
	Completed = "Completed",
	Failed = "Failed",
	Cancelled = "Cancelled",
}

export enum PaymentIntentType {
	Ach = "Ach",
	Book = "Book",
	Wire = "Wire",
}

export type IntentCounterparty = {
	name: string
	routingNumber?: string
	accountNumber?: string
	routingNumberLast4?: string
	accountNumberLast4?: string
}

export type PaymentIntent = {
	id: string
	type: "paymentIntent"
	attributes: {
		title: string
		description: string
		amount: number
		counterparty?: IntentCounterparty
		paymentType?: keyof typeof PaymentIntentType
		status: keyof typeof IntentStatus
		approvedAt?: string
		declinedAt?: string
		cancelledAt?: string
		failureReason?: string
		tags: Record<string, string>
		createdAt: string
		updatedAt: string
	}
	relationships?: {
		customer?: {data: {type: string; id: string}}
		org?: {data: {type: string; id: string}}
		account?: {data: {type: string; id: string}}
		payment?: {data: {type: string; id: string}}
		counterparty?: {data: {type: string; id: string}}
	}
}

export type FindIntentsParams = {
	accessToken: string
	offset: number
	limit: number
	sort?: string
	statuses?: string[]
	customerId?: string
}

export async function findIntents({
	accessToken,
	offset,
	limit,
	sort,
	statuses,
	customerId,
}: FindIntentsParams): Promise<Result<OkDocument<PaymentIntent[]>, ErrorDocument>> {
	const query = {
		page: {limit, offset},
		...(statuses && statuses.length > 0 ? {"filter[status]": statuses} : {}),
		...(customerId ? {"filter[customerId]": customerId} : {}),
		...(sort ? {sort} : {}),
		include: "customer,org,account,payment",
	}
	return await getResource<PaymentIntent[]>("intents", accessToken, query)
}

export async function getIntent(
	accessToken: string,
	id: string
): Promise<Result<OkDocument<PaymentIntent>, ErrorDocument>> {
	return await getResource<PaymentIntent>(`intents/${id}`, accessToken, {
		include: "customer,org,account,payment",
	})
}

/** Exactly one branch must be provided — existing counterparty by ID, or new inline details. */
type CounterpartyParam =
	| {
			counterpartyId: string
			counterpartyName?: never
			counterpartyRoutingNumber?: never
			counterpartyAccountNumber?: never
	  }
	| {
			counterpartyName: string
			counterpartyRoutingNumber?: string
			counterpartyAccountNumber?: string
			counterpartyId?: never
	  }

export type CreatePaymentIntentParams = {
	accessToken: string
	title: string
	description: string
	amountCents: number
	/** Provide exactly one of accountId or customerId — the API rejects both or neither. */
	accountId?: string
	customerId?: string
} & CounterpartyParam

export async function cancelIntent(
	accessToken: string,
	id: string
): Promise<Result<OkDocument<PaymentIntent>, ErrorDocument>> {
	return await createResource<PaymentIntent>(`intents/${id}/cancel`, accessToken, {
		type: "intent",
		attributes: {idempotencyKey: `dashboard-intent-cancel-${uuidv4()}`},
	})
}

export async function createPaymentIntent(
	params: CreatePaymentIntentParams
): Promise<Result<OkDocument<PaymentIntent>, ErrorDocument>> {
	const {accessToken, title, description, amountCents, accountId, customerId} = params

	const accountRelationship = accountId
		? {account: {data: {type: "account", id: accountId}}}
		: {customer: {data: {type: "customer", id: customerId}}}

	const counterpartyRelationship =
		"counterpartyId" in params && params.counterpartyId
			? {counterparty: {data: {type: "counterparty", id: params.counterpartyId}}}
			: {}

	const counterpartyAttributes =
		"counterpartyId" in params && params.counterpartyId
			? {}
			: {
					counterparty: {
						name: (params as {counterpartyName: string}).counterpartyName,
						...("counterpartyRoutingNumber" in params && params.counterpartyRoutingNumber
							? {routingNumber: params.counterpartyRoutingNumber}
							: {}),
						...("counterpartyAccountNumber" in params && params.counterpartyAccountNumber
							? {accountNumber: params.counterpartyAccountNumber}
							: {}),
					},
			  }

	const data: Record<string, unknown> = {
		type: "paymentIntent",
		attributes: {
			title,
			description,
			amount: amountCents,
			paymentType: "Ach",
			...counterpartyAttributes,
			idempotencyKey: `dashboard-intent-${uuidv4()}`,
		},
		relationships: {
			...accountRelationship,
			...counterpartyRelationship,
		},
	}
	return await createResource<PaymentIntent>("intents", accessToken, data)
}
